import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Vote, Lock, Plus, Trash2, Loader2, Radio, Tv, X, FlaskConical, RefreshCw,
} from "lucide-react";

const ELIGIBLE = ["news", "entertainment"];
const CATS = ["news", "entertainment", "sports", "music", "movies", "kids", "other"];

// ---------- Locked add-on card (shown when tenant lacks the entitlement) ----------
function LockedCard() {
  return (
    <div className="p-4 max-w-xl mx-auto mt-10" data-testid="magic-wall-locked">
      <div className="rounded-xl border border-border bg-card/60 p-8 text-center backdrop-blur">
        <div className="mx-auto w-14 h-14 rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center">
          <Lock className="w-6 h-6 text-fuchsia-400" />
        </div>
        <h1 className="font-display font-bold text-2xl mt-5">Magic Wall — Add-on</h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Build broadcast-grade interactive election maps — call regions live, track the
          electoral tally and burn a CNN-style results board straight into your on-air feed.
          Available on demand for news &amp; general-entertainment channels.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            className="rounded-sm tenant-btn"
            data-testid="magic-wall-request-access"
            onClick={() => toast.success("Request sent", { description: "Your account manager will enable the Magic Wall add-on shortly." })}
          >
            Request access
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => (window.location.href = "/console/pricing")} data-testid="magic-wall-see-plans">
            See plans
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MagicWall() {
  const { tenant } = useAuth();
  const enabled = !!tenant?.addons?.magic_wall;

  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [elections, setElections] = useState([]);
  const [electionId, setElectionId] = useState("");
  const [ev, setEv] = useState(null);
  const [activeCand, setActiveCand] = useState("clear");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [bugPreviewUrl, setBugPreviewUrl] = useState("");
  const [onAirBoard, setOnAirBoard] = useState(false);
  const [onAirBug, setOnAirBug] = useState(false);
  const [scorebugTarget, setScorebugTarget] = useState("national");
  const [newRegion, setNewRegion] = useState({ name: "", abbr: "", votes: "" });

  const channel = channels.find((c) => c.id === channelId) || null;
  const eligible = channel && ELIGIBLE.includes((channel.category || "").toLowerCase());

  useEffect(() => {
    if (!enabled) return;
    api.get("/channels").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setChannels(list);
      setChannelId((prev) => prev || list[0]?.id || "");
    }).catch(() => {});
  }, [enabled]);

  const loadElections = useCallback(async (cid) => {
    if (!cid) { setElections([]); return; }
    try {
      const r = await api.get(`/elections?channel_id=${cid}`);
      const list = Array.isArray(r.data) ? r.data : [];
      setElections(list);
      setElectionId((prev) => (list.some((e) => e.id === prev) ? prev : (list[0]?.id || "")));
    } catch { setElections([]); }
  }, []);

  useEffect(() => { if (eligible) loadElections(channelId); else setElections([]); }, [channelId, eligible, loadElections]);

  const loadElection = useCallback(async (id) => {
    if (!id) { setEv(null); return; }
    try { const r = await api.get(`/elections/${id}`); setEv(r.data); }
    catch { setEv(null); }
  }, []);

  useEffect(() => { loadElection(electionId); }, [electionId, loadElection]);

  // Blob preview (render.png needs auth → fetch via axios, object-URL it).
  const refreshPreview = useCallback(async (id) => {
    if (!id) { setPreviewUrl(""); return; }
    try {
      const r = await api.get(`/elections/${id}/render.png`, { responseType: "blob" });
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
    } catch { /* ignore */ }
  }, []);

  const refreshBugPreview = useCallback(async (id, target) => {
    if (!id) { setBugPreviewUrl(""); return; }
    try {
      const r = await api.get(`/elections/${id}/render.png`, { params: { mode: "scorebug", target: target || "national" }, responseType: "blob" });
      setBugPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (ev?.id) { refreshPreview(ev.id); refreshBugPreview(ev.id, scorebugTarget); } }, [ev, scorebugTarget, refreshPreview, refreshBugPreview]);

  // On-air status for this election on its channel (board + scorebug tracked separately).
  const refreshOnAir = useCallback(async (cid, eid) => {
    if (!cid || !eid) { setOnAirBoard(false); setOnAirBug(false); return; }
    try {
      const r = await api.get(`/graphics?channel_id=${cid}`);
      const layers = (r.data || []).filter((x) => x.type === "election" && (x.props || {}).election_id === eid);
      setOnAirBoard(layers.some((l) => l.visible && ((l.props || {}).mode || "board") !== "scorebug"));
      setOnAirBug(layers.some((l) => l.visible && ((l.props || {}).mode) === "scorebug"));
    } catch { setOnAirBoard(false); setOnAirBug(false); }
  }, []);
  useEffect(() => { refreshOnAir(channelId, electionId); }, [channelId, electionId, refreshOnAir]);

  const patchEv = async (patch) => {
    if (!ev) return;
    setEv((e) => ({ ...e, ...patch }));
    try { const r = await api.patch(`/elections/${ev.id}`, patch); setEv(r.data); }
    catch { toast.error("Save failed"); }
  };

  const setCategory = async (cat) => {
    if (!channel) return;
    try {
      const r = await api.patch(`/channels/${channel.id}`, { category: cat });
      setChannels((cs) => cs.map((c) => (c.id === channel.id ? { ...c, category: r.data.category } : c)));
      toast.success(`Channel tagged "${cat}"`);
    } catch { toast.error("Could not update channel category"); }
  };

  const createElection = async (template) => {
    if (!channelId) return;
    setBusy(true);
    try {
      const r = await api.post("/elections", {
        channel_id: channelId,
        name: template === "us" ? "US Presidential" : "New election",
        subtitle: "Live results",
        template,
      });
      await loadElections(channelId);
      setElectionId(r.data.id);
      toast.success("Election created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const deleteElection = async () => {
    if (!ev || !window.confirm(`Delete "${ev.name}"?`)) return;
    try {
      await api.delete(`/elections/${ev.id}`);
      setEv(null); setElectionId("");
      await loadElections(channelId);
      toast.success("Election deleted");
    } catch { toast.error("Delete failed"); }
  };

  const callRegion = async (region) => {
    if (!ev) return;
    const candidate_id = activeCand === "clear" ? null : activeCand;
    // optimistic
    setEv((e) => ({ ...e, regions: e.regions.map((r) => (r.id === region.id ? { ...r, called: candidate_id } : r)) }));
    try {
      const r = await api.post(`/elections/${ev.id}/regions/${region.id}/call`, { candidate_id });
      setEv(r.data);
    } catch { toast.error("Call failed"); loadElection(ev.id); }
  };

  const updateCandidate = (idx, patch) => {
    const candidates = ev.candidates.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    patchEv({ candidates });
  };
  const addCandidate = () => {
    if (ev.candidates.length >= 6) return;
    patchEv({ candidates: [...ev.candidates, { id: `c${Date.now()}`, name: `Candidate ${ev.candidates.length + 1}`, party: "", color: "#16A34A" }] });
  };
  const removeCandidate = (idx) => {
    if (ev.candidates.length <= 2) return;
    const removed = ev.candidates[idx].id;
    patchEv({
      candidates: ev.candidates.filter((_, i) => i !== idx),
      regions: ev.regions.map((r) => (r.called === removed ? { ...r, called: null } : r)),
    });
  };

  const addRegion = () => {
    if (!newRegion.name.trim()) return;
    patchEv({
      regions: [...ev.regions, {
        id: `r${Date.now()}`, name: newRegion.name.trim(),
        abbr: (newRegion.abbr || newRegion.name).slice(0, 4).toUpperCase(),
        votes: Number(newRegion.votes) || 0, row: 0, col: 0, called: null, reporting_pct: 0, vote_counts: {},
      }],
    });
    setNewRegion({ name: "", abbr: "", votes: "" });
  };
  const removeRegion = (id) => patchEv({ regions: ev.regions.filter((r) => r.id !== id) });

  const toggleOnAir = async (mode) => {
    if (!ev) return;
    const isBug = mode === "scorebug";
    const currentlyOn = isBug ? onAirBug : onAirBoard;
    setBusy(true);
    try {
      const layers = (await api.get(`/graphics?channel_id=${channelId}`)).data || [];
      const existing = layers.find((l) => l.type === "election"
        && ((l.props || {}).mode || "board") === (isBug ? "scorebug" : "board"));
      if (currentlyOn) {
        if (existing) await api.patch(`/graphics/${existing.id}`, { visible: false });
        toast.success(isBug ? "Scorebug off air" : "Election board off air");
      } else {
        const props = isBug
          ? { election_id: ev.id, mode: "scorebug", scorebug_target: scorebugTarget }
          : { election_id: ev.id, mode: "board" };
        if (existing) {
          await api.patch(`/graphics/${existing.id}`, { visible: true, props: { ...(existing.props || {}), ...props } });
        } else {
          await api.post("/graphics", {
            channel_id: channelId,
            layer_index: isBug ? 91 : 90,
            name: isBug ? "Election scorebug" : "Election map",
            type: "election",
            x: isBug ? 40 : 0, y: isBug ? 780 : 0,
            width: isBug ? 760 : 1920, height: isBug ? 260 : 1080,
            z_index: isBug ? 13 : 12, visible: true, opacity: 1.0,
            props,
          });
        }
        toast.success(isBug ? "Scorebug on air (bottom-left)" : "Election board on air (fullscreen)");
      }
      await refreshOnAir(channelId, ev.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change on-air state"); }
    finally { setBusy(false); }
  };

  const tally = useMemo(() => {
    const t = {};
    (ev?.regions || []).forEach((r) => { if (r.called) t[r.called] = (t[r.called] || 0) + (r.votes || 0); });
    return t;
  }, [ev]);
  const totalPossible = useMemo(() => (ev?.regions || []).reduce((s, r) => s + (r.votes || 0), 0) || 1, [ev]);

  const candColor = (id) => (ev?.candidates || []).find((c) => c.id === id)?.color || "#3A3A42";

  if (!enabled) return <LockedCard />;

  return (
    <div className="p-4 space-y-4" data-testid="magic-wall-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">§ News add-on</div>
          <h1 className="font-display font-bold text-2xl mt-1 flex items-center gap-2"><Vote className="w-5 h-5 text-fuchsia-400" /> Magic Wall</h1>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Channel</Label>
          <select value={channelId} onChange={(e) => setChannelId(e.target.value)}
            className="h-8 rounded-sm bg-background border border-border text-sm px-2" data-testid="magic-wall-channel-select">
            {channels.map((c) => <option key={c.id} value={c.id}>{c.name}{c.category ? ` · ${c.category}` : ""}</option>)}
          </select>
        </div>
      </div>

      {channel && !eligible && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-4" data-testid="magic-wall-ineligible">
          <div className="text-sm text-amber-200 font-medium">“{channel.name}” isn’t tagged as a news or entertainment channel.</div>
          <p className="text-xs text-muted-foreground mt-1">The Magic Wall is only available on news / general-entertainment channels. Tag this channel to continue:</p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" className="rounded-sm text-xs tenant-btn" onClick={() => setCategory("news")} data-testid="magic-wall-tag-news">Mark as News</Button>
            <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setCategory("entertainment")} data-testid="magic-wall-tag-entertainment">Mark as Entertainment</Button>
            <select defaultValue="" onChange={(e) => e.target.value && setCategory(e.target.value)} className="h-8 rounded-sm bg-background border border-border text-xs px-2" data-testid="magic-wall-cat-select">
              <option value="" disabled>Other category…</option>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      )}

      {eligible && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
          {/* LEFT — elections + interactive wall */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <select value={electionId} onChange={(e) => setElectionId(e.target.value)}
                className="h-8 rounded-sm bg-background border border-border text-sm px-2 min-w-[180px]" data-testid="magic-wall-election-select">
                <option value="">— Select election —</option>
                {elections.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.status})</option>)}
              </select>
              <Button size="sm" variant="outline" className="rounded-sm text-xs" disabled={busy} onClick={() => createElection(null)} data-testid="magic-wall-new-blank">
                <Plus className="w-3 h-3 mr-1" /> Blank
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm text-xs" disabled={busy} onClick={() => createElection("us")} data-testid="magic-wall-new-us">
                <FlaskConical className="w-3 h-3 mr-1" /> US template
              </Button>
              {ev && (
                <Button size="sm" variant="outline" className="rounded-sm text-xs border-rose-500/40 text-rose-400" onClick={deleteElection} data-testid="magic-wall-delete">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
            </div>

            {ev ? (
              <>
                {/* Event meta */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Title</Label>
                    <Input defaultValue={ev.name} onBlur={(e) => e.target.value !== ev.name && patchEv({ name: e.target.value })} className="h-8 text-sm rounded-sm" data-testid="magic-wall-name" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Win at</Label>
                    <Input type="number" defaultValue={ev.win_threshold} onBlur={(e) => Number(e.target.value) !== ev.win_threshold && patchEv({ win_threshold: Number(e.target.value) })} className="h-8 text-sm rounded-sm" data-testid="magic-wall-threshold" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</Label>
                    <select value={ev.status} onChange={(e) => patchEv({ status: e.target.value })} className="h-8 w-full rounded-sm bg-background border border-border text-sm px-2" data-testid="magic-wall-status">
                      <option value="draft">draft</option><option value="live">live</option><option value="closed">closed</option>
                    </select>
                  </div>
                  <div className="col-span-2 sm:col-span-4">
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Subtitle</Label>
                    <Input defaultValue={ev.subtitle} onBlur={(e) => e.target.value !== ev.subtitle && patchEv({ subtitle: e.target.value })} className="h-8 text-sm rounded-sm" data-testid="magic-wall-subtitle" />
                  </div>
                </div>

                {/* Candidates */}
                <div className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Candidates · click one to call regions for them</Label>
                    <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={addCandidate} data-testid="magic-wall-add-candidate"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setActiveCand("clear")} data-testid="magic-wall-cand-clear"
                      className={`rounded-sm border px-2 py-1.5 text-xs ${activeCand === "clear" ? "border-cyan-400 bg-cyan-400/10 text-cyan-100" : "border-border text-muted-foreground"}`}>Eraser (un-call)</button>
                    {ev.candidates.map((c, i) => (
                      <div key={c.id} className={`flex items-center gap-1 rounded-sm border px-1.5 py-1 ${activeCand === c.id ? "border-cyan-400 bg-cyan-400/10" : "border-border"}`} data-testid={`magic-wall-cand-${i}`}>
                        <input type="color" value={c.color} onChange={(e) => updateCandidate(i, { color: e.target.value })} className="w-6 h-6 rounded-sm bg-transparent border-0 cursor-pointer" data-testid={`magic-wall-cand-color-${i}`} />
                        <input value={c.name} onChange={(e) => setEv((s) => ({ ...s, candidates: s.candidates.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) }))}
                          onBlur={(e) => updateCandidate(i, { name: e.target.value })}
                          className="w-28 bg-transparent text-xs outline-none" data-testid={`magic-wall-cand-name-${i}`} />
                        <button onClick={() => setActiveCand(c.id)} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-muted" data-testid={`magic-wall-cand-select-${i}`}>Call</button>
                        {ev.candidates.length > 2 && <button onClick={() => removeCandidate(i)} className="text-rose-400/70 hover:text-rose-300" data-testid={`magic-wall-cand-remove-${i}`}><X className="w-3 h-3" /></button>}
                      </div>
                    ))}
                  </div>
                  {/* EV tally bar */}
                  <div>
                    <div className="h-5 w-full rounded-sm bg-muted/40 overflow-hidden flex" data-testid="magic-wall-tally-bar">
                      {ev.candidates.map((c) => {
                        const w = (tally[c.id] || 0) / totalPossible * 100;
                        return <div key={c.id} style={{ width: `${w}%`, background: c.color }} title={`${c.name}: ${tally[c.id] || 0}`} />;
                      })}
                    </div>
                    <div className="flex items-center justify-between text-[11px] mt-1 text-muted-foreground">
                      {ev.candidates.slice(0, 2).map((c) => <span key={c.id} style={{ color: c.color }} className="font-mono font-bold">{c.name}: {tally[c.id] || 0}</span>)}
                      <span className="font-mono">{ev.win_threshold} to win</span>
                    </div>
                  </div>
                </div>

                {/* Region grid (interactive wall) */}
                <div className="rounded-md border border-border p-3">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">The Wall · click a region to call it</Label>
                  <div className="mt-2 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(ev.grid_cols || 12, 12)}, minmax(0,1fr))` }} data-testid="magic-wall-grid">
                    {ev.regions.map((r) => {
                      const col = r.called ? candColor(r.called) : "transparent";
                      return (
                        <button key={r.id} onClick={() => callRegion(r)} data-testid={`magic-wall-region-${r.abbr || r.id}`}
                          className="relative rounded-sm border border-border/70 aspect-square flex flex-col items-center justify-center text-[11px] font-bold transition hover:ring-2 hover:ring-cyan-400/60"
                          style={{ background: r.called ? col : "rgba(255,255,255,0.03)", color: r.called ? "#fff" : "var(--foreground)" }}>
                          <span className="leading-none">{(r.abbr || r.name || "").toUpperCase()}</span>
                          <span className="text-[9px] opacity-80 leading-none mt-0.5">{r.votes}</span>
                        </button>
                      );
                    })}
                    {ev.regions.length === 0 && <div className="col-span-full text-xs text-muted-foreground py-6 text-center">No regions yet — add some below.</div>}
                  </div>
                  {/* Region management */}
                  <div className="mt-3 flex items-end gap-2 flex-wrap">
                    <div><Label className="text-[10px] uppercase text-muted-foreground">Region</Label><Input value={newRegion.name} onChange={(e) => setNewRegion((s) => ({ ...s, name: e.target.value }))} className="h-7 text-xs rounded-sm w-36" placeholder="Ohio" data-testid="magic-wall-region-name" /></div>
                    <div><Label className="text-[10px] uppercase text-muted-foreground">Abbr</Label><Input value={newRegion.abbr} onChange={(e) => setNewRegion((s) => ({ ...s, abbr: e.target.value }))} className="h-7 text-xs rounded-sm w-20" placeholder="OH" data-testid="magic-wall-region-abbr" /></div>
                    <div><Label className="text-[10px] uppercase text-muted-foreground">Votes</Label><Input type="number" value={newRegion.votes} onChange={(e) => setNewRegion((s) => ({ ...s, votes: e.target.value }))} className="h-7 text-xs rounded-sm w-20" placeholder="17" data-testid="magic-wall-region-votes" /></div>
                    <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={addRegion} data-testid="magic-wall-region-add"><Plus className="w-3 h-3 mr-1" /> Add region</Button>
                  </div>
                  {ev.regions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid="magic-wall-region-chips">
                      {ev.regions.map((r) => (
                        <span key={r.id} className="inline-flex items-center gap-1 rounded-sm border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {(r.abbr || r.name)}·{r.votes}
                          <button onClick={() => removeRegion(r.id)} className="text-rose-400/60 hover:text-rose-300" data-testid={`magic-wall-region-del-${r.abbr || r.id}`}><X className="w-2.5 h-2.5" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground py-10 text-center">Create or select an election to start.</div>
            )}
          </div>

          {/* RIGHT — on-air preview + controls (board + scorebug) */}
          <div className="space-y-3">
            <div className="rounded-md border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Tv className="w-3 h-3" /> Full board</Label>
                <button onClick={() => ev && refreshPreview(ev.id)} className="text-muted-foreground hover:text-foreground" data-testid="magic-wall-preview-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
              <div className="rounded-sm overflow-hidden border border-border bg-black aspect-video flex items-center justify-center" data-testid="magic-wall-preview">
                {previewUrl ? <img src={previewUrl} alt="Election map" className="w-full h-full object-contain" /> : <span className="text-xs text-muted-foreground">No preview</span>}
              </div>
              {ev && (
                <Button className={`w-full rounded-sm ${onAirBoard ? "bg-rose-500 hover:bg-rose-600 text-white" : "tenant-btn"}`} disabled={busy} onClick={() => toggleOnAir("board")} data-testid="magic-wall-toggle-onair">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Radio className="w-4 h-4 mr-1" />}
                  {onAirBoard ? "Take board off air" : "Put board on air (fullscreen)"}
                </Button>
              )}
              {onAirBoard && <Badge className="w-full justify-center bg-rose-500/15 text-rose-400 border border-rose-400/40 font-mono text-[10px] uppercase">● Board on air on {channel?.name}</Badge>}
            </div>

            <div className="rounded-md border border-border p-3 space-y-3">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Tv className="w-3 h-3" /> Single-race scorebug</Label>
              <select value={scorebugTarget} onChange={(e) => setScorebugTarget(e.target.value)}
                className="w-full h-8 rounded-sm bg-background border border-border text-sm px-2" data-testid="magic-wall-scorebug-target">
                <option value="national">National total (EV + X to win)</option>
                {(ev?.regions || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.abbr || "Region"}</option>)}
              </select>
              <div className="rounded-sm overflow-hidden border border-border bg-black flex items-center justify-center p-2" data-testid="magic-wall-scorebug-preview" style={{ minHeight: 90 }}>
                {bugPreviewUrl ? <img src={bugPreviewUrl} alt="Scorebug" className="w-full object-contain" /> : <span className="text-xs text-muted-foreground">No preview</span>}
              </div>
              {ev && (
                <Button className={`w-full rounded-sm ${onAirBug ? "bg-rose-500 hover:bg-rose-600 text-white" : "tenant-btn"}`} disabled={busy} onClick={() => toggleOnAir("scorebug")} data-testid="magic-wall-toggle-scorebug">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Radio className="w-4 h-4 mr-1" />}
                  {onAirBug ? "Take scorebug off air" : "Put scorebug on air (corner)"}
                </Button>
              )}
              {onAirBug && <Badge className="w-full justify-center bg-rose-500/15 text-rose-400 border border-rose-400/40 font-mono text-[10px] uppercase">● Scorebug on air on {channel?.name}</Badge>}
              <p className="text-[10px] text-muted-foreground">Board &amp; scorebug can both be on air. Reposition / resize either in the channel’s Graphics editor (layers “Election map” / “Election scorebug”).</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
